// Single source of truth for nLPD/GDPR cookie consent state.
// Consumers (analytics-loader, SpeedInsights gate, Registrazione consent record,
// CookieBanner UI) MUST import from here so the tri-state logic (accepted / declined / custom)
// stays consistent. Fix for P2 in audit.

export type ConsentCategory = "necessary" | "analytics" | "marketing";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_KEY = "swissez_cookie_consent";
const ANALYTICS_KEY = "swissez_cookie_analytics";
const MARKETING_KEY = "swissez_cookie_marketing";
export const CONSENT_EVENT = "swissez-consent-changed";

export function getConsentState(): ConsentState {
  if (typeof window === "undefined") return { necessary: true, analytics: false, marketing: false };
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw === "accepted") return { necessary: true, analytics: true, marketing: true };
    if (raw === "declined") return { necessary: true, analytics: false, marketing: false };
    if (raw === "custom") {
      return {
        necessary: true,
        analytics: localStorage.getItem(ANALYTICS_KEY) === "1",
        marketing: localStorage.getItem(MARKETING_KEY) === "1",
      };
    }
  } catch { /* localStorage disabled → treat as declined */ }
  return { necessary: true, analytics: false, marketing: false };
}

export function isAllowed(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  return getConsentState()[category];
}

export function writeCustom(state: { analytics: boolean; marketing: boolean }): void {
  try {
    localStorage.setItem(CONSENT_KEY, "custom");
    localStorage.setItem(ANALYTICS_KEY, state.analytics ? "1" : "0");
    localStorage.setItem(MARKETING_KEY, state.marketing ? "1" : "0");
  } catch { /* ignore */ }
}

export function writeAccepted(): void {
  try { localStorage.setItem(CONSENT_KEY, "accepted"); } catch { /* ignore */ }
}

export function writeDeclined(): void {
  try { localStorage.setItem(CONSENT_KEY, "declined"); } catch { /* ignore */ }
}

export function hasConsentDecision(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" || v === "custom";
  } catch { return false; }
}

/**
 * Subscribe to consent-changed events (both in-tab CustomEvent and cross-tab storage events).
 * Returns an unsubscribe function.
 */
export function subscribeConsent(handler: (state: ConsentState) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const fire = () => handler(getConsentState());
  window.addEventListener(CONSENT_EVENT, fire);
  window.addEventListener("storage", fire);
  return () => {
    window.removeEventListener(CONSENT_EVENT, fire);
    window.removeEventListener("storage", fire);
  };
}

export function dispatchConsent(state: ConsentState = getConsentState()): void {
  if (typeof window === "undefined") return;
  // Google Consent Mode v2 bridge
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", {
      ad_storage: state.marketing ? "granted" : "denied",
      analytics_storage: state.analytics ? "granted" : "denied",
      ad_user_data: state.marketing ? "granted" : "denied",
      ad_personalization: state.marketing ? "granted" : "denied",
    });
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
}
